import React, { useState, useMemo, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import http from '../../lib/http';
import { generateSharingUrl } from '../../lib/utils';
import { API_BASE_URL } from '../../lib/axiosConfig';
import AnimatedModal from '../ui/AnimatedModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '../../contexts/AuthContext';
import {
  XMarkIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';

// Normalize candidate to { id, firstName, lastName, email, mobile }
const normalizeCandidate = (c) => {
  const id = c.id ?? c._id;
  const email = c.email ?? '';
  const mobile = c.mobile ?? c.phone ?? '';
  let firstName = c.firstName ?? '';
  let lastName = c.lastName ?? '';
  if (!firstName && !lastName && c.name) {
    const parts = String(c.name).trim().split(/\s+/);
    firstName = parts[0] ?? '';
    lastName = parts.slice(1).join(' ') ?? '';
  }
  return { id, firstName, lastName, email, mobile };
};

// Replace placeholders in content
// context: optional { role, company, applyLink } for {{jobTitle}}, {{Role}}, {{Company}}, {{company name}}, {{applyLink}}
const replacePlaceholders = (content, candidate, context = {}) => {
  if (!content) return '';
  let out = String(content);
  const fullCandidateDisplayName = [candidate?.firstName, candidate?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const candidateDisplayName =
    fullCandidateDisplayName || candidate?.firstName || candidate?.lastName || '';
  const uploadDeadlineText =
    context?.documentUploadDeadlineText ||
    (context?.documentUploadDeadlineDays != null
      ? `${context.documentUploadDeadlineDays} days`
      : '180 days');
  const daysRemainingPreview =
    context?.documentUploadDaysRemainingPreview || 'approximately 175 days';
  const vars = {
    '{{First Name}}': candidate?.firstName,
    '{{Last Name}}': candidate?.lastName,
    '{{first name}}': candidate?.firstName,
    '{{last name}}': candidate?.lastName,
    '{{FIRST NAME}}': candidate?.firstName,
    '{{LAST NAME}}': candidate?.lastName,
    '{{First name}}': candidate?.firstName,
    '{{Last name}}': candidate?.lastName,
    '{{Candidate Name}}': candidateDisplayName,
    '{{candidate name}}': candidateDisplayName,
    '{{Role}}': context?.role,
    '{{Company}}': context?.company,
    '{{role}}': context?.role,
    '{{company}}': context?.company,
    '{{company name}}': context?.company,
    '{{companyname}}': context?.company,
    '{{Client Company Name}}': context?.company,
    '{{jobTitle}}': context?.role,
    '{{Job Title}}': context?.role,
    '{{jobrole}}': context?.role,
    '{{X days}}': uploadDeadlineText,
    '{{X days remaining}}': daysRemainingPreview,
    '{{X Days Remaining}}': daysRemainingPreview,
    '{{UPLOAD_LINK}}': context?.uploadLinkPreview || context?.uploadLink,
    '{{applyLink}}': context?.applyLink,
    '{{screeningLink}}': context?.applyLink,
    '{{bookingLink}}': context?.bookingLink,
    '{{interviewAddress}}': context?.interviewAddress,
    '{{slotDuration}}': context?.slotDuration,
    '{{interviewMode}}': context?.interviewMode,
  };
  Object.entries(vars).forEach(([key, val]) => {
    out = out.split(key).join(val ?? '');
  });
  return out;
};

// Email validation
const isValidEmail = (email) => {
  if (!email || !email.trim()) return false;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email.trim());
};

// Validate comma-separated emails
const validateEmails = (str) => {
  if (!str || !str.trim()) return { valid: true, invalid: [] };
  const emails = str
    .split(/[,;]/)
    .map((e) => e.trim())
    .filter(Boolean);
  const invalid = emails.filter((e) => !isValidEmail(e));
  return { valid: invalid.length === 0, invalid };
};

const BODY_TEMPLATES_DEFAULT = {
  Default: `<p>Dear {{First Name}},</p>
<p>Thank you for your interest in the {{jobTitle}} position at {{Company}}. We have reviewed your application and would like to move forward.</p>
<p>Please let us know your availability for the next steps.</p>
<p>Best regards,<br/>TalentCrux</p>`,
  Invite: `<p>Dear {{First Name}},</p>
<p>We are pleased to invite you to the next stage of our recruitment process for the {{jobTitle}} role at {{Company}}.</p>
<p>We would like to schedule an interview at your earliest convenience.</p>
<p>Please reply with your preferred dates and times.</p>
<p>Best regards,<br/>TalentCrux</p>`,
  Offer: `<p>Dear {{First Name}},</p>
<p>We are delighted to offer you the {{jobTitle}} position at {{Company}}!</p>
<p>We believe you would be a great addition to our team.</p>
<p>Please review the attached offer letter and let us know if you have any questions.</p>
<p>Best regards,<br/>TalentCrux</p>`,
};

const FOLLOW_TEMPLATES = {
  Default: `<p>Dear {{First Name}},</p>
<p>This is a gentle reminder to complete your screening for the {{jobTitle}} position at {{company name}}.</p>
<p>Please ignore this message if you have already completed the screening.</p>
<p>Please find the screening link below:<br/><a href="{{applyLink}}">{{applyLink}}</a></p>
<p>The screening takes approximately 30 minutes to complete. If you have any questions or require assistance, please feel free to reply to this email. We look forward to hearing from you.</p>
<p>Best regards,<br/>TalentCrux</p>`,
  Reminder: `<p>Hello {{First Name}},</p>
<p>This is a friendly reminder about our previous communication regarding the {{jobTitle}} position at {{Company}}. We're still interested in moving forward and would appreciate your response.</p>
<p>Best regards,<br/>TalentCrux</p>`,
  Final: `<p>Dear {{First Name}},</p>
<p>This is our final follow-up regarding your application for the {{jobTitle}} role at {{Company}}. If you're still interested, please reach out to us soon.</p>
<p>Best regards,<br/>TalentCrux</p>`,
};

const SHORTLIST_FOLLOW_TEMPLATES = {
  Default: `<p>Dear {{First Name}},</p>
<p>This is a quick follow-up regarding your shortlisted status for the {{jobTitle}} role at {{company name}}.</p>
<p>Our team is currently finalizing the next step and will reach out to you shortly.</p>
<p>If you have any immediate questions, please reply to this email.</p>
<p>Best regards,<br/>TalentCrux</p>`,
  Reminder: `<p>Dear {{First Name}},</p>
<p>Just a reminder that you are shortlisted for the {{jobTitle}} role at {{company name}}.</p>
<p>We are in the process of scheduling the next round and will share details soon.</p>
<p>Best regards,<br/>TalentCrux</p>`,
  Final: `<p>Dear {{First Name}},</p>
<p>Final follow-up regarding your shortlisted application for the {{jobTitle}} role at {{company name}}.</p>
<p>If you remain interested, please confirm by replying to this email so we can prioritize your next steps.</p>
<p>Best regards,<br/>TalentCrux</p>`,
};

const SendEmailToCandidatesModal = ({
  isOpen,
  onClose,
  selectedCandidates = [],
  modalTitle = 'Send Email to Candidates',
  defaultFromEmail = 'recruiter@talentcrux.ai',
  initialSubject,
  bodyTemplatesOverride,
  initialBodyHtml,
  initialFollowUp,
  initialIncludeSignature,
  screeningId,
  editingDraftId,
  onSaveDraft,
  emailContext,
  nextButtonLabel,
  sendButtonLabel = 'Send Email',
  roleName,
  companyName,
  onBack,
  onBeforeSend,
  onAfterSendSuccess,
  skipDefaultSend = false,
  extraPlaceholderContext,
  pendingChannelInvites,
  sendChannelInvitesForChannel,
  screeningName, // Added prop
}) => {
  const { user } = useAuth();
  const isEmailIntegrationEnabled = user?.features?.email_integration === true;
  const isEmailDisabled = pendingChannelInvites?.channels && pendingChannelInvites.channels.email === false;
  const isWhatsAppEnabled = pendingChannelInvites?.channels?.whatsapp === true;
  const isWhatsAppOnly = isEmailDisabled && isWhatsAppEnabled;

  const hasChannelInvites = !!pendingChannelInvites?.channels?.whatsapp;
  const channelLabels = hasChannelInvites
    ? [
      pendingChannelInvites.channels.whatsapp && 'WhatsApp',
    ].filter(Boolean)
    : [];
  const effectiveSendLabel = isWhatsAppOnly
    ? 'Send WhatsApp Invite'
    : hasChannelInvites
      ? `Send Email & ${channelLabels.join(' & ')}`
      : sendButtonLabel;
  const normalizedCandidates = useMemo(
    () => selectedCandidates.map(normalizeCandidate).filter((c) => isWhatsAppOnly ? (c.email || c.mobile) : c.email),
    [selectedCandidates, isWhatsAppOnly]
  );

  const candidatesWithMobile = useMemo(() => {
    return (pendingChannelInvites?.rows || [])
      .map((row) => ({
        ...row,
        mobileNormalized: (row.mobile || row.phone || '').replace(/[^\d+]/g, ''),
      }))
      .filter((r) => /^\+?[0-9]{7,15}$/.test(r.mobileNormalized));
  }, [pendingChannelInvites]);

  const effectiveBodyTemplates =
    bodyTemplatesOverride && Object.keys(bodyTemplatesOverride).length
      ? bodyTemplatesOverride
      : BODY_TEMPLATES_DEFAULT;

  const effectiveFollowTemplates = useMemo(() => {
    if (emailContext === 'shortlist') {
      return SHORTLIST_FOLLOW_TEMPLATES;
    }
    if (emailContext === 'invite' && screeningId) {
      const applyLink = generateSharingUrl(`apply/${screeningId}`);
      return {
        Default: `<p>Dear {{First Name}},</p>
<p>This is a gentle reminder to complete your screening for the {{jobTitle}} position at {{company name}}.</p>
<p>Please ignore this message if you have already completed the screening.</p>
<p>Please find the screening link below:<br/><a href={{applyLink}}>{{applyLink}}</a></p>
<p>The screening takes approximately 30 minutes to complete. If you have any questions or require assistance, please feel free to reply to this email. We look forward to hearing from you.</p>
<p>Best regards,<br/>TalentCrux</p>`,
        Reminder: `<p>Dear {{First Name}},</p>
<p>If you have not yet completed this assessment for the {{jobTitle}} position at {{Company}}, this is a friendly reminder to complete it. We would appreciate you doing so at your earliest convenience so that we may proceed with your application.</p>
<p>To complete your screening, please use the link below:</p>
<p><a href={{applyLink}}>{{applyLink}}</a></p><br/>
<p>Best regards,<br/>TalentCrux</p>`,
        Final: `<p>Dear {{First Name}},</p>
<p>If you have not yet completed this assessment for the {{jobTitle}} role at {{Company}}, this is our final follow-up. We kindly request you to complete it at your earliest convenience, as this round will close shortly.</p>
<p>To complete your screening, please use the link below:</p>
<p><a href={{applyLink}}>{{applyLink}}</a></p><br/>
<p>Best regards,<br/>TalentCrux</p>`,
      };
    }
    return FOLLOW_TEMPLATES;
  }, [emailContext, screeningId]);

  const templateNames = Object.keys(effectiveBodyTemplates);
  const defaultTemplateName = templateNames[0] || 'Default';
  const defaultBodyHtml = effectiveBodyTemplates[defaultTemplateName] || '';
  const defaultSubject = 'Congratulations – Next Step for {{jobrole}} Role at {{companyname}}';
  const baseSubject = initialSubject || defaultSubject;

  const [fromEmail, setFromEmail] = useState(defaultFromEmail);
  const [emailIntegration, setEmailIntegration] = useState({
    loading: true,
    connected: false,
    email: '',
  });
  const [subject, setSubject] = useState(baseSubject);
  const [showCC, setShowCC] = useState(false);
  const [showBCC, setShowBCC] = useState(false);
  const [cc, setCC] = useState('');
  const [bcc, setBCC] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(defaultTemplateName);
  const [bodyHtml, setBodyHtml] = useState(defaultBodyHtml);
  const defaultWhatsAppTemplate = `Dear {{First Name}},\n\nThank you for your interest in the *{{jobTitle}}* position at *{{company name}}*.\n\nWe are pleased to invite you to proceed to the next stage of the selection process — *{{screeningName}}*.\n\nPlease complete your screening using the link below:\n{{applyLink}}\n\nWe request that you set aside approximately 30 minutes to complete the screening. We recommend completing it within 2-3 days.\n\nReply to this message if you have any questions.\n\nBest regards,\nTalentCrux`;
  const [whatsAppTemplate, setWhatsAppTemplate] = useState(defaultWhatsAppTemplate);
  const defaultWhatsAppFollowUpTemplate = `Hi {{First Name}},\n\nThis is a gentle reminder regarding your {{screeningName}} application for the {{jobTitle}} role at {{company name}}.\n\nOur records indicate that there are still pending steps required to proceed with your application. Please use the secure link below to complete them at your earliest convenience:\n{{applyLink}}\n\nIf you encounter any technical issues or have questions, please contact us at support@talentcrux.ai.\n\nBest regards,\nTalentCrux Team`;
  const [whatsAppFollowUpTemplate, setWhatsAppFollowUpTemplate] = useState(defaultWhatsAppFollowUpTemplate);
  const [includeSignature, setIncludeSignature] = useState(false);
  const [needFollowUp, setNeedFollowUp] = useState(false);
  const [followUpSubject, setFollowUpSubject] = useState(
    'Follow-up — Shortlisted for {{jobTitle}} Role at {{company name}}'
  );
  const [followBodyHtml, setFollowBodyHtml] = useState('');
  const [followUpCount, setFollowUpCount] = useState(1);
  const [followUpFrequency, setFollowUpFrequency] = useState('2');
  const [followUpFrequencyType, setFollowUpFrequencyType] = useState('days'); // 'days' or 'minutes'
  const [selectedFollowTemplate, setSelectedFollowTemplate] = useState('Default');
  const [previewCandidateId, setPreviewCandidateId] = useState(normalizedCandidates[0]?.id ?? null);
  const [currentStep, setCurrentStep] = useState('compose');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [emailReviewExpanded, setEmailReviewExpanded] = useState(false);
  const [followUpContentExpanded, setFollowUpContentExpanded] = useState(false);
  const [resolvedJobTitle, setResolvedJobTitle] = useState('');
  const [resolvedCompanyName, setResolvedCompanyName] = useState('');
  const wasOpenRef = useRef(false);

  // When we have screeningId but roleName/companyName are missing (e.g. stale screening), fetch apply-info for jobTitle
  useEffect(() => {
    if (!isOpen || !screeningId) return;
    const needJobTitle = !roleName || (typeof roleName === 'string' && !roleName.trim());
    const needCompany = !companyName || (typeof companyName === 'string' && !companyName.trim());
    if (!needJobTitle && !needCompany) return;
    let cancelled = false;
    http
      .get(`/api/screenings/${screeningId}/apply-info`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        if (needJobTitle && (data.jobTitle || data.roleName))
          setResolvedJobTitle(String(data.jobTitle || data.roleName || '').trim());
        if (needCompany && data.companyName)
          setResolvedCompanyName(String(data.companyName || '').trim());
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, [isOpen, screeningId, roleName, companyName]);

  useEffect(() => {
    if (!isOpen) {
      setResolvedJobTitle('');
      setResolvedCompanyName('');
    }
  }, [isOpen]);

  const handleSaveDraft = async () => {
    if (!screeningId) return;
    const payload = {
      name: subject.trim().slice(0, 100) || 'Draft',
      subject: subject.trim(),
      bodyHtml,
      includeSignature,
      followUp: needFollowUp
        ? {
          enabled: true,
          subject: followUpSubject,
          html: followBodyHtml,
          whatsappTemplate: whatsAppFollowUpTemplate,
          count: followUpCount,
          frequency: parseInt(followUpFrequency, 10) || 2,
          frequencyType: followUpFrequencyType,
        }
        : { enabled: false },
    };
    try {
      setSavingDraft(true);
      if (currentDraftId) {
        await http.patch(`/api/screenings/${screeningId}/email-drafts/${currentDraftId}`, payload);
        toast.success('Draft updated');
      } else {
        const postPayload = emailContext === 'invite' ? { ...payload, context: 'invite' } : payload;
        const res = await http.post(`/api/screenings/${screeningId}/email-drafts`, postPayload);
        if (res?.data?.draft?._id) setCurrentDraftId(res.data.draft._id);
        toast.success('Draft saved');
      }
      if (typeof onSaveDraft === 'function') onSaveDraft();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to save draft';
      toast.error(msg);
    } finally {
      setSavingDraft(false);
    }
  };

  const previewCandidate =
    normalizedCandidates.find((c) => c.id === previewCandidateId) ?? normalizedCandidates[0];

  const ccValidation = validateEmails(cc);
  const bccValidation = validateEmails(bcc);

  const DEFAULT_SIGNATURE = `Regards,\nTalentCrux`;

  // Reset function to clear all form state
  const resetForm = () => {
    setSubject(baseSubject);
    setShowCC(false);
    setShowBCC(false);
    setCC('');
    setBCC('');
    setSelectedTemplate(defaultTemplateName);
    setBodyHtml(defaultBodyHtml);
    setIncludeSignature(false);
    setNeedFollowUp(false);
    setFollowUpSubject('Follow-up — Shortlisted for {{jobTitle}} Role at {{company name}}');
    setFollowBodyHtml('');
    setFollowUpCount(1);
    setFollowUpFrequency('2');
    setFollowUpFrequencyType('days');
    setSelectedFollowTemplate('Default');
    setCurrentStep('compose');
    setSendingEmail(false);
    if (normalizedCandidates.length > 0) {
      setPreviewCandidateId(normalizedCandidates[0]?.id ?? null);
    }
    setWhatsAppTemplate(defaultWhatsAppTemplate);
    setWhatsAppFollowUpTemplate(defaultWhatsAppFollowUpTemplate);
  };

  const handleConnectGmail = () => {
    const apiBase = API_BASE_URL || 'http://localhost:5001';
    const token = localStorage.getItem('token');
    window.location.href = `${apiBase}/api/integrations/google/connect?token=${encodeURIComponent(token)}`;
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isEmailIntegrationEnabled) {
      setEmailIntegration({
        loading: false,
        connected: false,
        email: '',
      });
      setFromEmail(defaultFromEmail);
      return;
    }
    let cancelled = false;
    setEmailIntegration((prev) => ({ ...prev, loading: true }));
    http.get('/api/integrations/email/status')
      .then((res) => {
        if (cancelled) return;
        if (res.data && res.data.connected) {
          setEmailIntegration({
            loading: false,
            connected: true,
            email: res.data.email,
          });
          setFromEmail(res.data.email);
        } else {
          setEmailIntegration({
            loading: false,
            connected: false,
            email: '',
          });
          setFromEmail('connect_gmail');
        }
      })
      .catch((err) => {
        console.error('Failed to fetch email status in modal:', err);
        if (!cancelled) {
          setEmailIntegration({
            loading: false,
            connected: false,
            email: '',
          });
          setFromEmail('connect_gmail');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, defaultFromEmail, isEmailIntegrationEnabled]);

  // Initialize subject/body/template when the modal opens — not on every parent re-render.
  // Parents often pass a new `selectedCandidates` array each render (e.g. inline .filter()),
  // which would otherwise retrigger this effect and reset the step (e.g. preview → compose)
  // while a nested confirm dialog is shown.
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) return;

    const effectiveSubject = initialSubject ?? baseSubject;
    const effectiveBody =
      initialBodyHtml != null && initialBodyHtml !== '' ? initialBodyHtml : defaultBodyHtml;
    setSubject(effectiveSubject);
    setSelectedTemplate(defaultTemplateName);
    setBodyHtml(effectiveBody);
    if (initialFollowUp && initialFollowUp.enabled) {
      setNeedFollowUp(true);
      setFollowUpSubject(
        initialFollowUp.subject ??
        (emailContext === 'shortlist'
          ? 'Follow-up — Shortlisted for {{jobTitle}} Role at {{company name}}'
          : 'Gentle Reminder – Screening Pending for {{jobTitle}} Role at {{company name}}.')
      );
      setFollowBodyHtml(
        initialFollowUp.html && initialFollowUp.html.trim() !== ''
          ? initialFollowUp.html
          : (effectiveFollowTemplates.Default ?? '')
      );
      setFollowUpCount(typeof initialFollowUp.count === 'number' ? initialFollowUp.count : 1);
      setFollowUpFrequency(String(initialFollowUp.frequency ?? 2));
      setFollowUpFrequencyType(initialFollowUp.frequencyType === 'minutes' ? 'minutes' : 'days');
    } else {
      // When nextButtonLabel is set (screening page), default to follow-up enabled so "Don't need followup" is unchecked and all config is visible
      setNeedFollowUp(!!nextButtonLabel);
      setFollowUpSubject(
        emailContext === 'shortlist'
          ? 'Follow-up — Shortlisted for {{jobTitle}} Role at {{company name}}'
          : 'Gentle Reminder – Screening Pending for {{jobTitle}} Role at {{company name}}.'
      );
      setFollowBodyHtml(
        nextButtonLabel && effectiveFollowTemplates.Default
          ? effectiveFollowTemplates.Default
          : ''
      );
      setFollowUpCount(1);
      setFollowUpFrequency('2');
      setFollowUpFrequencyType('days');
    }
    if (typeof initialIncludeSignature === 'boolean') {
      setIncludeSignature(initialIncludeSignature);
    }
    if (normalizedCandidates.length > 0) {
      setPreviewCandidateId(normalizedCandidates[0]?.id ?? null);
    }
    setCurrentStep('compose');
    setCurrentDraftId(editingDraftId ?? null);
    setWhatsAppTemplate(defaultWhatsAppTemplate);
    setWhatsAppFollowUpTemplate(defaultWhatsAppFollowUpTemplate);
  }, [
    isOpen,
    baseSubject,
    defaultTemplateName,
    defaultBodyHtml,
    initialSubject,
    initialBodyHtml,
    initialFollowUp,
    initialIncludeSignature,
    normalizedCandidates,
    editingDraftId,
    nextButtonLabel,
    effectiveFollowTemplates,
    emailContext,
  ]);

  // If candidates arrive after open, keep preview dropdown valid without re-running full init.
  useEffect(() => {
    if (!isOpen || normalizedCandidates.length === 0) return;
    setPreviewCandidateId((prev) => {
      if (prev != null && normalizedCandidates.some((c) => c.id === prev)) return prev;
      return normalizedCandidates[0]?.id ?? null;
    });
  }, [isOpen, normalizedCandidates]);

  // Initialize followBodyHtml when follow-up is enabled
  useEffect(() => {
    if (needFollowUp && !followBodyHtml && effectiveFollowTemplates.Default) {
      setFollowBodyHtml(effectiveFollowTemplates.Default);
    }
  }, [needFollowUp, effectiveFollowTemplates]);

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'link',
  ];

  const handleSelectTemplate = (name) => {
    setSelectedTemplate(name);
    if (effectiveBodyTemplates[name]) {
      setBodyHtml(effectiveBodyTemplates[name]);
    }
  };

  const handleSelectFollowTemplate = (name) => {
    setSelectedFollowTemplate(name);
    if (effectiveFollowTemplates[name]) {
      setFollowBodyHtml(effectiveFollowTemplates[name]);
    }
  };

  const emailPlaceholderContext = useMemo(() => {
    const role =
      (roleName && String(roleName).trim()) || (resolvedJobTitle && String(resolvedJobTitle).trim()) || '';
    const company =
      (companyName && String(companyName).trim()) ||
      (resolvedCompanyName && String(resolvedCompanyName).trim()) ||
      'Indara Technologies Private Limited';
    const applyLink = screeningId ? generateSharingUrl(`apply/${screeningId}`) : '';
    const base = { role, company, applyLink };
    const extra =
      extraPlaceholderContext && typeof extraPlaceholderContext === 'object'
        ? extraPlaceholderContext
        : {};
    return { ...base, ...extra };
  }, [roleName, companyName, resolvedJobTitle, resolvedCompanyName, screeningId, extraPlaceholderContext]);

  const renderWhatsAppContent = (candidate) => {
    const jobTitle = emailPlaceholderContext.role || 'Role';
    const companyName = emailPlaceholderContext.company || 'Company';
    const activeScreeningName = screeningName || 'Screening';
    const applyLink = emailPlaceholderContext.applyLink || 'https://www.talentcrux.ai/apply/...';
    const firstName = (candidate?.firstName || '').trim() || 'there';

    let text = whatsAppTemplate;
    text = text
      .replace(/\{\{First Name\}\}/gi, firstName)
      .replace(/\{\{jobTitle\}\}/gi, jobTitle)
      .replace(/\{\{company name\}\}/gi, companyName)
      .replace(/\{\{screeningName\}\}/gi, activeScreeningName)
      .replace(/\{\{applyLink\}\}/gi, applyLink);

    const parts = text.split(/(\*[^*]+\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('*') && part.endsWith('*')) {
        return <strong key={idx}>{part.slice(1, -1)}</strong>;
      }
      return part;
    });
  };

  const renderWhatsAppFollowUpContent = (candidate) => {
    const jobTitle = emailPlaceholderContext.role || 'Role';
    const companyName = emailPlaceholderContext.company || 'Company';
    const activeScreeningName = screeningName || 'Screening';
    const applyLink = emailPlaceholderContext.applyLink || 'https://www.talentcrux.ai/apply/...';
    const firstName = (candidate?.firstName || '').trim() || 'there';

    let text = whatsAppFollowUpTemplate;
    text = text
      .replace(/\{\{First Name\}\}/gi, firstName)
      .replace(/\{\{jobTitle\}\}/gi, jobTitle)
      .replace(/\{\{company name\}\}/gi, companyName)
      .replace(/\{\{screeningName\}\}/gi, activeScreeningName)
      .replace(/\{\{applyLink\}\}/gi, applyLink);

    const parts = text.split(/(\*[^*]+\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('*') && part.endsWith('*')) {
        return <strong key={idx}>{part.slice(1, -1)}</strong>;
      }
      return part;
    });
  };

  const getPreviewBody = (candidate) => {
    let html = replacePlaceholders(bodyHtml, candidate, emailPlaceholderContext);
    if (includeSignature) {
      const sig = DEFAULT_SIGNATURE.replace(/\n/g, '<br/>');
      html += `<br/><br/><p>${sig}</p>`;
    }
    return html;
  };

  const getPreviewSubject = (candidate) =>
    replacePlaceholders(subject, candidate, emailPlaceholderContext);
  const getFollowUpSubject = (candidate) =>
    replacePlaceholders(followUpSubject, candidate, emailPlaceholderContext);
  const getFollowUpBody = (candidate) =>
    replacePlaceholders(followBodyHtml, candidate, emailPlaceholderContext);

  const handleSendEmails = async () => {
    if (normalizedCandidates.length === 0) {
      toast.error('No candidates selected');
      return;
    }
    if (isWhatsAppOnly && candidatesWithMobile.length === 0) {
      toast.error('None of the selected candidates have a valid mobile number for WhatsApp outreach.');
      return;
    }
    if (fromEmail === 'connect_gmail' && !isWhatsAppOnly) {
      toast.error('Please connect your Gmail account or select the default platform email to proceed.');
      return;
    }
    try {
      setSendingEmail(true);
      const emailData = {
        subject,
        html: bodyHtml,
        cc: showCC && cc ? cc : undefined,
        bcc: showBCC && bcc ? bcc : undefined,
        fromEmail: fromEmail,
        includeSignature,
      };
      if (typeof onBeforeSend === 'function' && skipDefaultSend) {
        emailData.followUp = needFollowUp
          ? {
            enabled: true,
            subject: followUpSubject,
            html: followBodyHtml,
            whatsappTemplate: whatsAppFollowUpTemplate,
            count: parseInt(followUpCount, 10) || 1,
            frequency: parseInt(followUpFrequency, 10) || 1,
            frequencyType: followUpFrequencyType === 'minutes' ? 'minutes' : 'days',
          }
          : { enabled: false };
      }
      if (typeof onBeforeSend === 'function') {
        await onBeforeSend(emailData);
      } else if (skipDefaultSend) {
        toast.error('Missing send handler');
        return;
      }
      if (!skipDefaultSend && !isWhatsAppOnly) {
        await http.post('/api/emails/send', {
          screeningId,
          candidates: normalizedCandidates.map((candidate) => ({
            _id: candidate.id,
            email: candidate.email,
            firstName: candidate.firstName,
            lastName: candidate.lastName,
          })),
          email: {
            subject: subject,
            html: bodyHtml,
            cc: showCC && cc ? cc : undefined,
            bcc: showBCC && bcc ? bcc : undefined,
            fromEmail: fromEmail,
            includeSignature,
            roleName: (roleName && String(roleName).trim()) || resolvedJobTitle || undefined,
            companyName:
              (companyName && String(companyName).trim()) || resolvedCompanyName || undefined,
            applyLink: emailPlaceholderContext.applyLink || undefined,
          },
          followUp: needFollowUp
            ? {
              enabled: true,
              subject: followUpSubject,
              html: followBodyHtml,
              whatsappTemplate: whatsAppFollowUpTemplate,
              count: followUpCount,
              frequency: parseInt(followUpFrequency),
              frequencyType: followUpFrequencyType, // 'days' or 'minutes'
            }
            : {
              enabled: false,
            },
        });
        toast.success(
          `Emails sent to ${normalizedCandidates.length} candidate(s)${needFollowUp ? ` with ${followUpCount} follow-up(s) scheduled` : ''
          }`
        );
      }
      // Fire pending SMS/WhatsApp channel invites after email is sent
      if (hasChannelInvites && typeof sendChannelInvitesForChannel === 'function') {
        const recipients = (pendingChannelInvites.rows || [])
          .map((row) => {
            const name =
              row.name ||
              [row.firstName, row.lastName]
                .map((part) => (part || '').trim())
                .filter(Boolean)
                .join(' ') ||
              row.email ||
              '';
            const mobile = (row.mobile || row.phone || '').replace(/[^\d+]/g, '');
            return { name, mobile, _id: row.id || row._id, email: row.email };
          })
          .filter((r) => /^\+?[0-9]{7,15}$/.test(r.mobile));
        if (recipients.length > 0) {
          for (const channel of ['whatsapp']) {
            if (!pendingChannelInvites.channels[channel]) continue;
            const channelLabel = 'WhatsApp';
            try {
              const followUpData = needFollowUp
                ? {
                  enabled: true,
                  subject: followUpSubject,
                  html: followBodyHtml,
                  whatsappTemplate: whatsAppFollowUpTemplate,
                  count: parseInt(followUpCount, 10) || 1,
                  frequency: parseInt(followUpFrequency, 10) || 1,
                  frequencyType: followUpFrequencyType === 'minutes' ? 'minutes' : 'days',
                }
                : { enabled: false };
              const summary = await sendChannelInvitesForChannel(channel, recipients, whatsAppTemplate, followUpData);
              if (summary?.sent) {
                toast.success(
                  `${summary.sent} ${channelLabel} invite${summary.sent === 1 ? '' : 's'} sent`
                );
              }
              if (summary?.failed) {
                toast.error(
                  `${summary.failed} ${channelLabel} invite${summary.failed === 1 ? '' : 's'} failed`
                );
              }
            } catch (channelErr) {
              toast.error(channelErr?.response?.data?.message || `Failed to send ${channelLabel} invites`);
            }
          }
        }
      }
      if (typeof onAfterSendSuccess === 'function') {
        await onAfterSendSuccess();
      }
      if (emailContext === 'invite' && screeningId && !isWhatsAppOnly) {
        const payload = {
          name: subject.trim().slice(0, 100) || 'Draft',
          subject: subject.trim(),
          bodyHtml,
          includeSignature,
          followUp: needFollowUp
            ? {
              enabled: true,
              subject: followUpSubject,
              html: followBodyHtml,
              whatsappTemplate: whatsAppFollowUpTemplate,
              count: followUpCount,
              frequency: parseInt(followUpFrequency, 10) || 2,
              frequencyType: followUpFrequencyType,
            }
            : { enabled: false },
        };
        if (currentDraftId) {
          http
            .patch(`/api/screenings/${screeningId}/email-drafts/${currentDraftId}`, payload)
            .catch(() => { });
        } else {
          http
            .post(`/api/screenings/${screeningId}/email-drafts`, {
              ...payload,
              context: 'invite',
            })
            .catch(() => { });
        }
      }
      resetForm();
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.details?.response ||
        err?.response?.data?.details?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to send emails';
      toast.error(msg);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 'compose') {
      if (fromEmail === 'connect_gmail') {
        toast.error('Please connect your Gmail account or select the default platform email to proceed.');
        return;
      }
      setCurrentStep('follow-up');
    } else if (currentStep === 'follow-up') {
      setCurrentStep('final');
    }
  };

  const handleBack = () => {
    if (currentStep === 'follow-up') {
      setCurrentStep('compose');
    } else if (currentStep === 'final') {
      setCurrentStep('follow-up');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      backdropClassName="bg-black/40"
      contentClassName="w-11/12 md:w-5/6 lg:w-4/5 xl:w-3/4 mx-auto border rounded-lg bg-white max-h-[95vh] overflow-hidden flex flex-col"
      zIndex={50}
    >
      <div className="relative p-0 flex flex-col flex-1 min-h-0 overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 data-testid="email-modal-heading" className="text-xl font-semibold text-gray-900">{modalTitle}</h3>
              <p className="mt-1 text-sm text-gray-500">
                {normalizedCandidates.length} candidate
                {normalizedCandidates.length !== 1 ? 's' : ''} selected
                {normalizedCandidates.length > 0 && (
                  <span className="ml-1 text-slate-600">
                    —{' '}
                    {normalizedCandidates
                      .map((c) => c.firstName || c.email)
                      .filter(Boolean)
                      .slice(0, 3)
                      .join(', ')}
                    {normalizedCandidates.length > 3 ? '…' : ''}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-6 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body - Different steps */}
        {currentStep === 'compose' ? (
          <div className="flex-1 overflow-hidden flex flex-col md:flex-row min-h-0">
            {/* Left: Composer */}
            {isWhatsAppOnly ? (
              <div className="flex-1 overflow-y-auto p-5 border-r border-gray-100 bg-slate-50/30">
                <div className="space-y-4 max-w-md">
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <DevicePhoneMobileIcon className="h-5 w-5 text-emerald-600" />
                      <span className="font-semibold text-emerald-800 text-sm">WhatsApp Outreach</span>
                    </div>
                    <p className="text-xs text-emerald-700 leading-relaxed">
                      You have selected WhatsApp as your only communication channel. Candidates will receive an automated invitation message on their WhatsApp number.
                    </p>
                  </div>
                  {candidatesWithMobile.length < normalizedCandidates.length && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 leading-relaxed">
                      <strong>Warning:</strong> {normalizedCandidates.length - candidatesWithMobile.length} of the selected {normalizedCandidates.length} candidate(s) do not have a valid mobile number and will not receive the WhatsApp invite.
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                      WhatsApp Message Template
                      <span className="bg-slate-100 text-slate-500 text-[10px] font-medium px-1.5 py-0.5 rounded border border-slate-200">
                        Read-only
                      </span>
                    </label>
                    <textarea
                      value={whatsAppTemplate}
                      readOnly
                      rows={14}
                      className="w-full p-4 bg-slate-50 border border-slate-200 rounded-md text-xs font-mono text-slate-500 cursor-not-allowed focus:outline-none focus:ring-0 leading-relaxed"
                    />
                    <p className="mt-1.5 text-[10px] text-slate-400">
                      To ensure reliable delivery, WhatsApp templates cannot be edited directly. Placeholders will be dynamically replaced for each candidate.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-5 border-r border-gray-100 bg-slate-50/30">
                <div className="space-y-4 max-w-md">
                  {/* From */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">From</label>
                    {emailIntegration.loading ? (
                      <div className="w-full h-9 bg-slate-100 animate-pulse rounded-md border border-slate-200" />
                    ) : emailIntegration.connected ? (
                      <select
                        value={fromEmail}
                        onChange={(e) => setFromEmail(e.target.value)}
                        className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-md bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#007BFF] transition-all"
                      >
                        <option value={emailIntegration.email}>{emailIntegration.email} (Connected Google Account)</option>
                        <option value={defaultFromEmail}>{defaultFromEmail} (Default Platform Mail)</option>
                      </select>
                    ) : (
                      <div className="space-y-2">
                        <select
                          value={fromEmail}
                          onChange={(e) => setFromEmail(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-md bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#007BFF] transition-all"
                        >
                          {isEmailIntegrationEnabled && (
                            <option value="connect_gmail">Connect Gmail (Not Connected)</option>
                          )}
                          <option value={defaultFromEmail}>{defaultFromEmail} (Default Platform Mail)</option>
                        </select>
                        {fromEmail === 'connect_gmail' && (
                          <button
                            type="button"
                            onClick={handleConnectGmail}
                            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-md transition shadow-sm"
                          >
                            <svg className="h-4 w-4" viewBox="0 0 24 24">
                              <path
                                fill="#ffffff"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              />
                              <path
                                fill="#ffffff"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              />
                              <path
                                fill="#ffffff"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                              />
                              <path
                                fill="#ffffff"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                              />
                            </svg>
                            <span>Connect your Gmail</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Subject + CC/BCC */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-medium text-slate-600">Subject</label>
                      <div className="flex items-center gap-1.5">
                        {!showCC ? (
                          <button
                            type="button"
                            onClick={() => setShowCC(true)}
                            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-800 px-2 py-0.5 rounded-md hover:bg-slate-100 transition-colors"
                          >
                            <PlusIcon className="h-3 w-3" />
                            CC
                          </button>
                        ) : (
                          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-indigo-50">
                            <span className="text-xs font-medium text-indigo-700">CC</span>
                            <button
                              type="button"
                              onClick={() => {
                                setShowCC(false);
                                setCC('');
                              }}
                              className="text-indigo-500 hover:text-indigo-700"
                              title="Remove CC"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        {!showBCC ? (
                          <button
                            type="button"
                            onClick={() => setShowBCC(true)}
                            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-800 px-2 py-0.5 rounded-md hover:bg-slate-100 transition-colors"
                          >
                            <PlusIcon className="h-3 w-3" />
                            BCC
                          </button>
                        ) : (
                          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-indigo-50">
                            <span className="text-xs font-medium text-indigo-700">BCC</span>
                            <button
                              type="button"
                              onClick={() => {
                                setShowBCC(false);
                                setBCC('');
                              }}
                              className="text-indigo-500 hover:text-indigo-700"
                              title="Remove BCC"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="e.g. Congratulations – Next Step for {{jobTitle}} Role at {{Company}}"
                      className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-slate-400">
                      Use {'{{jobTitle}}'} and {'{{Company}}'} for job title and company (filled from context when available).
                    </p>
                    {(showCC || showBCC) && (
                      <div className="mt-2 space-y-1.5">
                        {showCC && (
                          <div>
                            <input
                              type="text"
                              value={cc}
                              onChange={(e) => setCC(e.target.value)}
                              placeholder="CC: email1@example.com, email2@example.com"
                              className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 ${!ccValidation.valid ? 'border-red-300 focus:ring-red-500' : 'border-slate-200 focus:ring-indigo-500 focus:border-transparent'}`}
                            />
                            {!ccValidation.valid && ccValidation.invalid.length > 0 && (
                              <p className="text-xs text-red-600 mt-0.5">
                                Invalid: {ccValidation.invalid.join(', ')}
                              </p>
                            )}
                          </div>
                        )}
                        {showBCC && (
                          <div>
                            <input
                              type="text"
                              value={bcc}
                              onChange={(e) => setBCC(e.target.value)}
                              placeholder="BCC: email1@example.com, email2@example.com"
                              className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 ${!bccValidation.valid ? 'border-red-300 focus:ring-red-500' : 'border-slate-200 focus:ring-indigo-500 focus:border-transparent'}`}
                            />
                            {!bccValidation.valid && bccValidation.invalid.length > 0 && (
                              <p className="text-xs text-red-600 mt-0.5">
                                Invalid: {bccValidation.invalid.join(', ')}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Body Templates — hide for invite flow (only Invite body is used, no tabs) */}
                  {emailContext !== 'invite' && templateNames.length > 1 && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-medium text-slate-600">
                          Body Templates
                        </label>
                        <div className="flex items-center gap-1">
                          {templateNames.map((name) => (
                            <button
                              key={name}
                              type="button"
                              onClick={() => handleSelectTemplate(name)}
                              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${selectedTemplate === name
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                            >
                              {name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Email Body Editor */}
                  {!isWhatsAppOnly && (
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Email Body
                      </label>
                      <div className="border border-slate-200 rounded-md bg-white overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-transparent">
                        <ReactQuill
                          theme="snow"
                          value={bodyHtml}
                          onChange={setBodyHtml}
                          modules={quillModules}
                          formats={quillFormats}
                          className="min-h-[200px] [&_.ql-container]:min-h-[160px] [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:pb-2 [&_.ql-container]:border-0"
                        />
                      </div>
                      <p className="mt-1.5 text-xs text-slate-400">
                        Use {'{{First Name}}'} — replaced per candidate. Use {'{{jobTitle}}'} and {'{{Company}}'} for job title and company.
                      </p>
                    </div>
                  )}

                  {/* WhatsApp Template Editor */}
                  {isWhatsAppEnabled && (
                    <div className="mt-4">
                      <label className="block text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1.5">
                        WhatsApp Template
                        <span className="bg-slate-100 text-slate-500 text-[10px] font-medium px-1.5 py-0.5 rounded border border-slate-200">
                          Read-only
                        </span>
                      </label>
                      <div className="border border-slate-200 rounded-md bg-slate-50 overflow-hidden">
                        <textarea
                          value={whatsAppTemplate}
                          readOnly
                          className="w-full px-3 py-2 text-sm text-slate-500 bg-transparent border-0 focus:ring-0 focus:outline-none resize-y min-h-[160px] cursor-not-allowed"
                        />
                      </div>
                      <p className="mt-1.5 text-xs text-slate-400">
                        To ensure delivery according to WhatsApp's anti-spam rules, this pre-approved template cannot be modified.
                      </p>
                    </div>
                  )}

                  {/* Signature */}
                  {/* <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="include-signature"
                    checked={includeSignature}
                    onChange={(e) => setIncludeSignature(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label
                    htmlFor="include-signature"
                    className="text-xs text-slate-600 cursor-pointer"
                  >
                    Include sender&apos;s email signature
                  </label>
                </div> */}
                </div>
              </div>
            )}

            {/* Right: Preview */}
            <div className="flex-[1.5] w-full flex flex-col border-t md:border-t-0 md:border-l border-gray-100 bg-white">
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {normalizedCandidates.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      Preview as
                    </label>
                    <select
                      value={previewCandidateId ?? ''}
                      onChange={(e) => setPreviewCandidateId(e.target.value || null)}
                      className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      {normalizedCandidates.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.firstName || c.lastName
                            ? `${c.firstName} ${c.lastName}`.trim()
                            : c.email}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {previewCandidate ? (
                  <div className="space-y-4">
                    {!isWhatsAppOnly && (
                      <div className="rounded-md border border-slate-200 bg-gradient-to-br from-slate-50 to-white px-4 py-3 text-sm shadow-sm">
                        <div className="space-y-1.5 mb-3 pb-3 border-b border-slate-200">
                          <div className="text-slate-700">
                            <span className="text-xs font-medium text-slate-500">To:</span>{' '}
                            <span className="font-medium">{previewCandidate.email}</span>
                          </div>
                          <div className="text-slate-700">
                            <span className="text-xs font-medium text-slate-500">Subject:</span>{' '}
                            <span className="font-medium">{getPreviewSubject(previewCandidate)}</span>
                          </div>
                        </div>
                        <div
                          className="prose prose-sm max-w-none text-slate-700 [&_p]:mb-2 [&_p:last-child]:mb-0"
                          dangerouslySetInnerHTML={{ __html: getPreviewBody(previewCandidate) }}
                        />
                      </div>
                    )}
                    {isWhatsAppEnabled && (
                      <div className="rounded-xl border border-slate-200 bg-[#efeae2] p-4 text-sm shadow-sm relative flex flex-col min-h-[300px]">
                        <div className="flex items-center gap-2 pb-2 mb-3 border-b border-slate-200/50">
                          <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                          <span className="text-xs font-semibold text-slate-600">WhatsApp Preview</span>
                          <span className="text-xs text-slate-400 ml-auto">
                            To: {previewCandidate.firstName || previewCandidate.lastName ? `${previewCandidate.firstName} ${previewCandidate.lastName}`.trim() : previewCandidate.email}
                            {previewCandidate.mobile ? ` (${previewCandidate.mobile})` : ' (No phone)'}
                          </span>
                        </div>
                        <div className="flex-1 flex flex-col justify-start">
                          {/* WhatsApp Chat Bubble */}
                          <div className="self-start max-w-[85%] bg-white rounded-lg rounded-tl-none px-4 py-3 shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] relative text-slate-800 text-sm leading-relaxed whitespace-pre-wrap">
                            {/* Little triangle on top left */}
                            <div className="absolute top-0 -left-2 w-0 h-0 border-t-[10px] border-t-white border-l-[10px] border-l-transparent" />
                            {renderWhatsAppContent(previewCandidate)}
                            <div className="text-[10px] text-slate-400 text-right mt-1.5">
                              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">
                    {isWhatsAppOnly ? 'No candidates with mobile/email to preview.' : 'No candidates with email to preview.'}
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : currentStep === 'follow-up' ? (
          <div className="flex-1 overflow-hidden flex flex-col md:flex-row min-h-0">
            {/* Left: Follow-up Configuration */}
            <div className="flex-1 overflow-y-auto p-5 border-r border-gray-100 bg-slate-50/30">
              <div className="space-y-4 max-w-md">
                {/* Follow-up Checkbox */}
                <div className="flex items-center gap-3 py-3 border-b border-slate-200 mb-4">
                  <input
                    type="checkbox"
                    id="need-follow-up-review"
                    checked={nextButtonLabel ? !needFollowUp : needFollowUp}
                    onChange={(e) =>
                      setNeedFollowUp(nextButtonLabel ? !e.target.checked : e.target.checked)
                    }
                    className="h-5 w-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
                  />
                  <label
                    htmlFor="need-follow-up-review"
                    className="text-base font-semibold text-slate-900 cursor-pointer leading-tight"
                  >
                    {nextButtonLabel ? 'Don\'t need follow-up' : 'Do you need a follow up?'}
                  </label>
                </div>

                {needFollowUp && (
                  <>
                    {/* Follow Templates — hidden in invite flow */}
                    {emailContext !== 'invite' && (
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="block text-xs font-medium text-slate-600">
                            Follow Templates
                          </label>
                          <div className="flex items-center gap-1">
                            {Object.keys(effectiveFollowTemplates).map((name) => (
                              <button
                                key={name}
                                type="button"
                                onClick={() => handleSelectFollowTemplate(name)}
                                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${selectedFollowTemplate === name
                                  ? 'bg-indigo-600 text-white shadow-sm'
                                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                  }`}
                              >
                                {name}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* How many follow ups + Frequency on one line */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">
                          How many follow ups?
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="2"
                          value={followUpCount}
                          onChange={(e) =>
                            setFollowUpCount(Math.max(1, Math.min(2, parseInt(e.target.value) || 1)))
                          }
                          className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">
                          Frequency
                        </label>
                        <select
                          value={followUpFrequency}
                          onChange={(e) => {
                            setFollowUpFrequency(e.target.value);
                            setFollowUpFrequencyType('days');
                          }}
                          className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="2">In 2 Days</option>
                          <option value="5">In 5 Days</option>
                          <option value="7">In 7 Days</option>
                          <option value="10">In 10 Days</option>
                        </select>
                      </div>
                    </div>

                    {!isWhatsAppOnly && (
                      <>
                        {/* Follow-up Subject */}
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1.5">
                            Follow-up Subject
                          </label>
                          <input
                            type="text"
                            value={followUpSubject}
                            onChange={(e) => setFollowUpSubject(e.target.value)}
                            placeholder="e.g. Gentle Reminder – Screening Pending for {{jobTitle}} Role at {{company name}}."
                            className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          />
                        </div>

                        {/* Follow Body Editor */}
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1.5">
                            Follow Body
                          </label>
                          <div className="border border-slate-200 rounded-md bg-white overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-transparent">
                            <ReactQuill
                              theme="snow"
                              value={followBodyHtml}
                              onChange={setFollowBodyHtml}
                              modules={quillModules}
                              formats={quillFormats}
                              className="min-h-[200px] [&_.ql-container]:min-h-[160px] [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:pb-2 [&_.ql-container]:border-0"
                            />
                          </div>
                          <p className="mt-1.5 text-xs text-slate-400">
                            Use {'{{First Name}}'} — replaced per candidate
                          </p>
                        </div>
                      </>
                    )}

                    {/* WhatsApp Follow-up Template Editor */}
                    {isWhatsAppEnabled && (
                      <div className="mt-4">
                        <label className="block text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1.5">
                          WhatsApp Follow-up Template
                          <span className="bg-slate-100 text-slate-500 text-[10px] font-medium px-1.5 py-0.5 rounded border border-slate-200">
                            Read-only
                          </span>
                        </label>
                        <div className="border border-slate-200 rounded-md bg-slate-50 overflow-hidden">
                          <textarea
                            value={whatsAppFollowUpTemplate}
                            readOnly
                            className="w-full px-3 py-2 text-sm text-slate-500 bg-transparent border-0 focus:ring-0 focus:outline-none resize-y min-h-[160px] cursor-not-allowed"
                          />
                        </div>
                        <p className="mt-1.5 text-xs text-slate-400">
                          To ensure delivery according to WhatsApp's anti-spam rules, this pre-approved template cannot be modified.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
              {/* When "Don't need followup" is checked (invite flow), show message centered in modal */}
              {!needFollowUp && nextButtonLabel && (
                <div className="min-h-[320px] w-full flex items-center justify-center py-12 px-4">
                  <p className="text-slate-600 text-center text-base max-w-sm">
                    No need to do anything please click next button
                  </p>
                </div>
              )}

            </div>

            {/* Right: Preview — only show when follow-up is enabled (invite flow) or not in invite flow */}
            {(needFollowUp || !nextButtonLabel) && (
              <div className="flex-[1.5] w-full flex flex-col border-t md:border-t-0 md:border-l border-gray-100 bg-white">
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {normalizedCandidates.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Preview as
                      </label>
                      <select
                        value={previewCandidateId ?? ''}
                        onChange={(e) => setPreviewCandidateId(e.target.value || null)}
                        className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        {normalizedCandidates.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.firstName || c.lastName
                              ? `${c.firstName} ${c.lastName}`.trim()
                              : c.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {previewCandidate && needFollowUp ? (
                    <div className="space-y-4">
                      {!isWhatsAppOnly && (
                        <div className="rounded-md border border-slate-200 bg-gradient-to-br from-slate-50 to-white px-4 py-3 text-sm shadow-sm">
                          <div className="space-y-1.5 mb-3 pb-3 border-b border-slate-200">
                            <div className="text-slate-700">
                              <span className="text-xs font-medium text-slate-500">To:</span>{' '}
                              <span className="font-medium">{previewCandidate.email}</span>
                            </div>
                            <div className="text-slate-700">
                              <span className="text-xs font-medium text-slate-500">Subject:</span>{' '}
                              <span className="font-medium">{getFollowUpSubject(previewCandidate)}</span>
                            </div>
                          </div>
                          <div
                            className="prose prose-sm max-w-none text-slate-700 [&_p]:mb-2 [&_p:last-child]:mb-0"
                            dangerouslySetInnerHTML={{
                              __html: getFollowUpBody(previewCandidate),
                            }}
                          />
                        </div>
                      )}
                      {isWhatsAppEnabled && (
                        <div className="rounded-xl border border-slate-200 bg-[#efeae2] p-4 text-sm shadow-sm relative flex flex-col min-h-[300px]">
                          <div className="flex items-center gap-2 pb-2 mb-3 border-b border-slate-200/50">
                            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="text-xs font-semibold text-slate-600">WhatsApp Follow-up Preview</span>
                            <span className="text-xs text-slate-400 ml-auto">
                              To: {previewCandidate.firstName || previewCandidate.lastName ? `${previewCandidate.firstName} ${previewCandidate.lastName}`.trim() : previewCandidate.email}
                              {previewCandidate.mobile ? ` (${previewCandidate.mobile})` : ' (No phone)'}
                            </span>
                          </div>
                          <div className="flex-1 flex flex-col justify-start">
                            {/* WhatsApp Chat Bubble */}
                            <div className="self-start max-w-[85%] bg-white rounded-lg rounded-tl-none px-4 py-3 shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] relative text-slate-800 text-sm leading-relaxed whitespace-pre-wrap">
                              {/* Little triangle on top left */}
                              <div className="absolute top-0 -left-2 w-0 h-0 border-t-[10px] border-t-white border-l-[10px] border-l-transparent" />
                              {renderWhatsAppFollowUpContent(previewCandidate)}
                              <div className="text-[10px] text-slate-400 text-right mt-1.5">
                                {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : needFollowUp ? (
                    <p className="text-xs text-slate-400">Enable follow-up to see preview.</p>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Final Review Step */
          <div className="flex-1 overflow-y-auto p-4">
            <div className="max-w-6xl mx-auto space-y-3">
              {/* Email Summary */}
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-50 to-slate-50 px-4 py-2.5 border-b border-slate-200">
                  <h4 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    <EnvelopeIcon className="h-4 w-4 text-indigo-600" />
                    Email Summary
                  </h4>
                </div>

                {hasChannelInvites && (
                  <div className="mx-4 mt-3 mb-0 flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg">
                    <DevicePhoneMobileIcon className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                    <span className="text-xs font-medium text-emerald-800">
                      {channelLabels.join(' & ')} invite{channelLabels.length > 1 ? 's' : ''} will also be sent to candidates with a valid mobile number
                    </span>
                  </div>
                )}

                <div className="p-4">
                  {needFollowUp ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                          <h5 className="text-xs font-semibold text-slate-700 mb-2 pb-1.5 border-b border-slate-200">
                            Recipients
                          </h5>
                          <div className="space-y-1.5">
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-medium text-slate-500 min-w-[45px]">
                                From:
                              </span>
                              <span className="text-xs text-slate-900 font-medium break-all">
                                {fromEmail}
                              </span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-medium text-slate-500 min-w-[45px]">
                                To:
                              </span>
                              <span className="text-xs text-slate-900 font-medium">
                                {normalizedCandidates.length} candidate
                                {normalizedCandidates.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            {showCC && cc && (
                              <div className="flex items-start gap-2">
                                <span className="text-xs font-medium text-slate-500 min-w-[45px]">
                                  CC:
                                </span>
                                <span className="text-xs text-slate-900 font-medium break-all">
                                  {cc}
                                </span>
                              </div>
                            )}
                            {showBCC && bcc && (
                              <div className="flex items-start gap-2">
                                <span className="text-xs font-medium text-slate-500 min-w-[45px]">
                                  BCC:
                                </span>
                                <span className="text-xs text-slate-900 font-medium break-all">
                                  {bcc}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                          <h5 className="text-xs font-semibold text-slate-700 mb-2 pb-1.5 border-b border-slate-200">
                            Email Details
                          </h5>
                          <div className="space-y-1.5">
                            <div>
                              <span className="text-xs font-medium text-slate-500">Subject:</span>
                              <span className="text-xs text-slate-900 font-medium break-words ml-1.5">
                                {subject}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium text-slate-500">
                                  Template:
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                                  {selectedTemplate}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium text-slate-500">
                                  Signature:
                                </span>
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${includeSignature
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-slate-100 text-slate-600'
                                    }`}
                                >
                                  {includeSignature ? 'Included' : 'Not included'}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium text-slate-500">
                                  Follow Up:
                                </span>
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${needFollowUp
                                    ? 'bg-indigo-100 text-indigo-700'
                                    : 'bg-slate-100 text-slate-600'
                                    }`}
                                >
                                  {needFollowUp ? 'Enabled' : 'Disabled'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                          <h5 className="text-xs font-semibold text-indigo-900 mb-2 pb-1.5 border-b border-indigo-200 flex items-center gap-1.5">
                            <CheckCircleIcon className="h-3.5 w-3.5 text-indigo-600" />
                            Follow-up Configuration
                          </h5>
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium text-slate-500">Count:</span>
                                <span className="text-sm text-slate-900 font-semibold">
                                  {followUpCount}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium text-slate-500">
                                  Frequency:
                                </span>
                                <span className="text-sm text-slate-900 font-semibold">
                                  In {followUpFrequency}{' '}
                                  {followUpFrequencyType === 'minutes' ? 'Minutes' : 'Days'}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium text-slate-500">Template:</span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                                {selectedFollowTemplate}
                              </span>
                            </div>
                            <div>
                              <span className="text-xs font-medium text-slate-500 block mb-1">
                                Subject:
                              </span>
                              <span className="text-xs text-slate-900 font-medium break-words">
                                {followUpSubject}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                        <h5 className="text-xs font-semibold text-slate-700 mb-2 pb-1.5 border-b border-slate-200">
                          Recipients
                        </h5>
                        <div className="space-y-1.5">
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-medium text-slate-500 min-w-[45px]">
                              From:
                            </span>
                            <span className="text-xs text-slate-900 font-medium break-all">
                              {fromEmail}
                            </span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-medium text-slate-500 min-w-[45px]">
                              To:
                            </span>
                            <span className="text-xs text-slate-900 font-medium">
                              {normalizedCandidates.length} candidate
                              {normalizedCandidates.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          {showCC && cc && (
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-medium text-slate-500 min-w-[45px]">
                                CC:
                              </span>
                              <span className="text-xs text-slate-900 font-medium break-all">
                                {cc}
                              </span>
                            </div>
                          )}
                          {showBCC && bcc && (
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-medium text-slate-500 min-w-[45px]">
                                BCC:
                              </span>
                              <span className="text-xs text-slate-900 font-medium break-all">
                                {bcc}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                        <h5 className="text-xs font-semibold text-slate-700 mb-2 pb-1.5 border-b border-slate-200">
                          Email Details
                        </h5>
                        <div className="space-y-1.5">
                          <div>
                            <span className="text-xs font-medium text-slate-500">Subject:</span>
                            <span className="text-xs text-slate-900 font-medium break-words ml-1.5">
                              {subject}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium text-slate-500">Template:</span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                                {selectedTemplate}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium text-slate-500">Signature:</span>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${includeSignature
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-slate-100 text-slate-600'
                                  }`}
                              >
                                {includeSignature ? 'Included' : 'Not included'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium text-slate-500">Follow Up:</span>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${needFollowUp
                                  ? 'bg-indigo-100 text-indigo-700'
                                  : 'bg-slate-100 text-slate-600'
                                  }`}
                              >
                                {needFollowUp ? 'Enabled' : 'Disabled'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Content Preview */}
              <div
                className={`grid gap-4 ${needFollowUp ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}
              >
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setEmailReviewExpanded((e) => !e)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50/80 transition-colors"
                  >
                    <h5 className="text-sm font-semibold text-slate-900">
                      Email Review Content
                    </h5>
                    {emailReviewExpanded ? (
                      <ChevronUpIcon className="h-5 w-5 text-slate-500 flex-shrink-0" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-slate-500 flex-shrink-0" />
                    )}
                  </button>
                  {emailReviewExpanded && (
                    <div className="px-4 pb-4 pt-0 space-y-2 border-t border-slate-100">
                      {previewCandidate ? (
                        <>
                          <div className="text-xs text-slate-600">
                            <span className="font-medium">To:</span> {previewCandidate.email}
                          </div>
                          <div className="text-xs text-slate-600">
                            <span className="font-medium">Subject:</span>{' '}
                            {getPreviewSubject(previewCandidate)}
                          </div>
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <div
                              className="prose prose-sm max-w-none text-slate-700 text-xs [&_p]:mb-2 [&_p:last-child]:mb-0 [&_p]:text-xs [&_li]:text-xs [&_strong]:text-xs [&_em]:text-xs"
                              dangerouslySetInnerHTML={{ __html: getPreviewBody(previewCandidate) }}
                            />
                          </div>
                        </>
                      ) : (
                        <p className="text-xs text-slate-400">No preview candidate available.</p>
                      )}
                    </div>
                  )}
                </div>

                {needFollowUp && (
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setFollowUpContentExpanded((e) => !e)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50/80 transition-colors"
                    >
                      <h5 className="text-sm font-semibold text-slate-900">
                        Follow up Content
                      </h5>
                      {followUpContentExpanded ? (
                        <ChevronUpIcon className="h-5 w-5 text-slate-500 flex-shrink-0" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-slate-500 flex-shrink-0" />
                      )}
                    </button>
                    {followUpContentExpanded && (
                      <div className="px-4 pb-4 pt-0 space-y-2 border-t border-slate-100">
                        {previewCandidate ? (
                          <>
                            <div className="text-xs text-slate-600">
                              <span className="font-medium">To:</span> {previewCandidate.email}
                            </div>
                            <div className="text-xs text-slate-600">
                              <span className="font-medium">Subject:</span>{' '}
                              {getFollowUpSubject(previewCandidate)}
                            </div>
                            <div className="mt-3 pt-3 border-t border-slate-200">
                              <div
                                className="prose prose-sm max-w-none text-slate-700 text-xs [&_p]:mb-2 [&_p:last-child]:mb-0 [&_p]:text-xs [&_li]:text-xs [&_strong]:text-xs [&_em]:text-xs"
                                dangerouslySetInnerHTML={{
                                  __html: getFollowUpBody(previewCandidate),
                                }}
                              />
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-slate-400">No preview candidate available.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-lg">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={currentStep === 'compose' ? (onBack || onClose) : handleBack}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
            >
              {currentStep !== 'compose' && <ChevronLeftIcon className="h-4 w-4" />}
              {currentStep === 'compose' ? (emailContext === 'invite' ? 'Back' : 'Cancel') : 'Back'}
            </button>
            <div className="flex items-center gap-2">
              {currentStep !== 'final' ? (
                <button
                  type="button"
                  data-testid={currentStep === 'compose' ? 'email-next-btn' : 'email-preview-btn'}
                  onClick={handleNext}
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  {currentStep === 'compose' && nextButtonLabel
                    ? nextButtonLabel
                    : currentStep === 'follow-up' && nextButtonLabel
                      ? (isWhatsAppOnly ? 'Preview invite' : 'Preview email')
                      : 'Next'}
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="email-send-btn"
                  onClick={handleSendEmails}
                  disabled={sendingEmail || normalizedCandidates.length === 0}
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                  {sendingEmail ? 'Sending…' : effectiveSendLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default SendEmailToCandidatesModal;
